import { useCallback, useEffect, useRef, useState } from "react";

const SAVE_DELAY_MS = 700;

const getErrorMessage = (error, fallback) =>
  error.response?.data?.error || error.message || fallback;

const useOnboardingDraft = ({
  api,
  data,
  enabled,
  autosaveEnabled = enabled,
  loadDraftEnabled = enabled,
  hasContent,
  onRestore,
  ready,
  requestName,
}) => {
  const [initialized, setInitialized] = useState(false);
  const [status, setStatus] = useState("");
  const timerRef = useRef(null);
  const saveQueueRef = useRef(Promise.resolve());
  const initializedRef = useRef(false);
  const restoreRef = useRef(onRestore);

  restoreRef.current = onRestore;

  useEffect(() => {
    if (!ready || initializedRef.current) return undefined;
    if (!enabled || !loadDraftEnabled) {
      initializedRef.current = true;
      setInitialized(true);
      return undefined;
    }
    let active = true;

    const loadDraft = async () => {
      try {
        const response = await api.getDraft();
        const draft = response.data?.data ?? response.data ?? null;
        if (!active) return;
        if (draft?.data) {
          const shouldRestore = window.confirm(
            `A saved ${requestName} request was found. Select OK to restore it, or Cancel to discard it and start over.`,
          );
          if (shouldRestore) {
            restoreRef.current(draft.data);
            setStatus(
              draft.data.filesToReattach?.length
                ? `Draft restored. Re-attach: ${draft.data.filesToReattach.join(", ")}.`
                : "Draft restored",
            );
          } else {
            await api.deleteDraft();
          }
        }
      } catch (error) {
        if (active) {
          setStatus(
            `Draft could not be loaded: ${getErrorMessage(error, "please try again.")}`,
          );
        }
      } finally {
        if (active) {
          initializedRef.current = true;
          setInitialized(true);
        }
      }
    };

    loadDraft();
    return () => {
      active = false;
    };
  }, [api, enabled, loadDraftEnabled, ready, requestName]);

  useEffect(() => {
    if (!autosaveEnabled || !initialized || !ready || !hasContent) {
      return undefined;
    }
    timerRef.current = setTimeout(() => {
      setStatus("Saving draft…");
      saveQueueRef.current = saveQueueRef.current
        .catch(() => undefined)
        .then(() => api.saveDraft(data))
        .then(() => setStatus("Draft saved"))
        .catch((error) => {
          setStatus(
            `Draft could not be saved: ${getErrorMessage(error, "please try again.")}`,
          );
        });
    }, SAVE_DELAY_MS);

    return () => clearTimeout(timerRef.current);
  }, [api, autosaveEnabled, data, hasContent, initialized, ready]);

  const clearDraft = useCallback(async () => {
    clearTimeout(timerRef.current);
    await saveQueueRef.current.catch(() => undefined);
    try {
      await api.deleteDraft();
      setStatus("");
      return true;
    } catch (error) {
      setStatus(
        `Draft could not be cleared: ${getErrorMessage(error, "please try again.")}`,
      );
      return false;
    }
  }, [api]);

  const saveDraft = useCallback(async () => {
    clearTimeout(timerRef.current);
    if (!enabled || !initialized || !ready) return false;

    setStatus("Saving draft…");
    const saveOperation = saveQueueRef.current
      .catch(() => undefined)
      .then(() => api.saveDraft(data));
    saveQueueRef.current = saveOperation;
    try {
      await saveOperation;
      setStatus("Draft saved");
      return true;
    } catch (error) {
      setStatus(
        `Draft could not be saved: ${getErrorMessage(error, "please try again.")}`,
      );
      return false;
    }
  }, [api, data, enabled, initialized, ready]);

  return {
    canSaveDraft: Boolean(enabled && initialized && ready),
    clearDraft,
    initialized,
    saveDraft,
    status,
  };
};

export default useOnboardingDraft;
